import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription} from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ProjectDetailsApiService } from '../../services/project-details-api.service';
import { ProjectDetailsStateService } from '../../services/project-details-state.service';
import { ProjectDetailsCascadeService } from '../../services/project-details-cascade.service';
import { URLPaths } from '../../../common/utilities/urlPaths.config';
import { commonFunctions } from '../../../common/classes/common.class';
import { ThemeAllocationDailogComponent } from '../../project-details-dialog/theme-allocation-dailog/theme-allocation-dailog.component';
import { ProjectDetailsMode, ProjectDetailsTableData } from '../../models/project-details.types';

@Component({
  selector: 'app-project-info-tab',
  templateUrl: './project-info-tab.component.html',
  styleUrls: ['./project-info-tab.component.scss']
})
export class ProjectInfoTabComponent implements OnInit, OnDestroy {

  mode: ProjectDetailsMode = 'add';

  projectForm!: FormGroup;
  themeForm!: FormGroup;

  clientsList: any[] = [];
  projectsList: string[] = [];

  projectCodeOptions: Array<{
    label: string;
    value: string;
    disabled?: boolean;
  }> = [];

  availableContactsList: string[] = [];
  employeesList: string[] = [];
  companiesList: any[] = [];
  projectTypesList: any[] = [];

  shortCode: string = '';

  countriesList: any[] = [];
  statesList: any[] = [];

  allStatesList: any[] = [];

  themesList: string[] = [];

  private rawClientData: any = {};

  isAddOtherDetailsDisabled = true;
  isViewOtherDetailsDisabled = true;

  private subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    public apiService: ProjectDetailsApiService,
    public stateService: ProjectDetailsStateService,
    public cascadeService: ProjectDetailsCascadeService
  ) {}

  ngOnInit(): void {
    this.buildForms();
    this.subscribeToState();
    this.loadReferenceData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  buildForms(): void {

    this.projectForm = this.fb.group({
      client: [null, Validators.required],
      project: [null],
      projectCode: ['', [Validators.required, Validators.maxLength(4)]],
      shortCode: [''],
      projectDescription: ['', Validators.required],
      projectManager: [null, Validators.required],
      billingCompany: [null, Validators.required],
      projectType: [null, Validators.required],
      paymentDays: [30, [Validators.required, Validators.min(0)]],
      internal: [false],
      oldCode: [false],
      oldProject: [''],
      contactMode: ['SEL-CONT', Validators.required],
      availableContacts: [null],
      contact: ['', Validators.required],
      address: [''],
      telephone: [''],
      country: [null, Validators.required],
      state: [null]
    });

    this.themeForm = this.fb.group({
      theme: [null, Validators.required],
      allocation: [null, [Validators.required, Validators.min(0), Validators.max(100)]]
    });

    this.projectForm.valueChanges.subscribe(() => {
      if (this.projectForm.dirty) {
        this.stateService.setUnsavedChanges(true);
      }
    });

    this.themeForm.valueChanges.subscribe(() => {
      if (this.themeForm.dirty) {
        this.stateService.setUnsavedChanges(true);
      }
    });
  }

  subscribeToState(): void {
    this.subs.add(
      this.stateService.mode$.subscribe(mode => {
        this.mode = mode;
        this.configureFormForMode(mode);
        this.cdr.detectChanges();
      })
    );

    this.subs.add(
      this.stateService.isAddOtherDetailsDisabled$.subscribe(disabled => {
        this.isAddOtherDetailsDisabled = disabled;
      })
    );

    this.subs.add(
      this.stateService.isViewOtherDetailsDisabled$.subscribe(disabled => {
        this.isViewOtherDetailsDisabled = disabled;
      })
    );
  }

  private normalizeValue(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private isUnitedStates(country: any): boolean {
    const normalizedCountry = this.normalizeValue(country);
    return ['united states of america', 'united states', 'usa', 'us', 'u.s.a.'].includes(normalizedCountry);
  }

  private getCountryFromStateItem(item: any): string {
    if (!item) return '';
    return (
      item.countryDescription ?? item.CountryDescription ?? item.countryName ??
      item.CountryName ?? item.Country_Name ?? item.country ?? item.Country ?? ''
    ).toString().trim();
  }

  private getStateFromStateItem(item: any): string {
    if (!item) return '';
    return (
      item.stateDescription ?? item.StateDescription ?? item.stateName ??
      item.StateName ?? item.State_Name ?? item.state ?? item.State ?? ''
    ).toString().trim();
  }

  async loadReferenceData(): Promise<void> {
    try {
      const [clients, companies, types, countriesData, employees, themes] = await Promise.all([
        this.apiService.getAllClients(),
        this.apiService.getCompanies(),
        this.apiService.getProjectTypes(),
        this.apiService.getCountriesAndStates(),
        this.apiService.getEmployees(),
        this.apiService.getProjectThemes()
      ]);

      this.clientsList = (clients || [])
        .map((c: any) => {
          if (typeof c === 'string') return c;
          const name = c.ClientName ?? c.Client_Name ?? c.clientName ?? c.Client ?? '';
          const code = c.Client ?? c.client ?? '';
          return name && code && name !== code ? `${name} - ${code}` : (name || code);
        })
        .filter(Boolean);

      this.companiesList = (companies || [])
        .map((co: any) => co.company ?? co.Company_Name ?? co.CompanyName ?? co)
        .filter(Boolean);

      this.projectTypesList = (types || [])
        .map((type: any) => type.projectType ?? type.ProjectType ?? type.Type_Name ?? type)
        .filter(Boolean);

      this.employeesList = (employees || [])
        .map((e: any) => typeof e === 'string' ? e : e.Employee ?? e.employee ?? e)
        .filter(Boolean);

      this.themesList = (themes || [])
        .map((th: any) => typeof th === 'string' ? th : th.Theme ?? th.theme ?? th)
        .filter(Boolean);

      if (Array.isArray(countriesData)) {
        this.allStatesList = countriesData;
        this.countriesList = Array.from(
          new Set(countriesData.map((item: any) => this.getCountryFromStateItem(item)).filter(Boolean))
        );
      }

      await this.cascadeService.initializeTaxAndCurrencyMaps();
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error loading project reference data:', e);
    }
  }

  configureFormForMode(mode: ProjectDetailsMode): void {
    const client = this.projectForm.get('client');
    const project = this.projectForm.get('project');
    const projectCode = this.projectForm.get('projectCode');
    const projectDescription = this.projectForm.get('projectDescription');
    const projectType = this.projectForm.get('projectType');
    const projectManager = this.projectForm.get('projectManager');
    const billingCompany = this.projectForm.get('billingCompany');
    const paymentDays = this.projectForm.get('paymentDays');
    const internal = this.projectForm.get('internal');
    const oldCode = this.projectForm.get('oldCode');

    const contactMode = this.projectForm.get('contactMode');
    const availableContacts = this.projectForm.get('availableContacts');
    const contact = this.projectForm.get('contact');
    const address = this.projectForm.get('address');
    const telephone = this.projectForm.get('telephone');
    const country = this.projectForm.get('country');
    const state = this.projectForm.get('state');

    const clientSelected = !!client?.value;
    const projectSelected = !!project?.value;

    if (mode === 'edit') {

      this.projectForm.get('project')
        ?.setValidators([Validators.required]);

      // ==========================================
      // STATE 1: NO CLIENT SELECTED
      // ==========================================
      if (!clientSelected) {

        client?.enable({ emitEvent: false });
        project?.disable({ emitEvent: false });

        projectCode?.disable({ emitEvent: false });
        projectDescription?.disable({ emitEvent: false });
        projectType?.disable({ emitEvent: false });
        projectManager?.disable({ emitEvent: false });
        billingCompany?.disable({ emitEvent: false });
        paymentDays?.disable({ emitEvent: false });
        internal?.disable({ emitEvent: false });
        oldCode?.disable({ emitEvent: false });

        contactMode?.disable({ emitEvent: false });
        availableContacts?.disable({ emitEvent: false });
        contact?.disable({ emitEvent: false });
        address?.disable({ emitEvent: false });
        telephone?.disable({ emitEvent: false });
        country?.disable({ emitEvent: false });
        state?.disable({ emitEvent: false });

      }

      // ==========================================
      // STATE 2: CLIENT SELECTED, NO PROJECT
      // ==========================================
      else if (!projectSelected) {

        client?.enable({ emitEvent: false });
        project?.enable({ emitEvent: false });

        projectCode?.disable({ emitEvent: false });
        projectDescription?.disable({ emitEvent: false });
        projectType?.disable({ emitEvent: false });
        projectManager?.disable({ emitEvent: false });
        billingCompany?.disable({ emitEvent: false });
        paymentDays?.disable({ emitEvent: false });
        internal?.disable({ emitEvent: false });
        oldCode?.disable({ emitEvent: false });

        contactMode?.disable({ emitEvent: false });
        availableContacts?.disable({ emitEvent: false });
        contact?.disable({ emitEvent: false });
        address?.disable({ emitEvent: false });
        telephone?.disable({ emitEvent: false });
        country?.disable({ emitEvent: false });
        state?.disable({ emitEvent: false });

      }

      // ==========================================
      // STATE 3: CLIENT + PROJECT SELECTED
      // ==========================================
      else {

        client?.enable({ emitEvent: false });
        project?.enable({ emitEvent: false });

        // Project Code should NEVER be editable
        projectCode?.disable({ emitEvent: false });

        // Project fields editable
        projectDescription?.enable({ emitEvent: false });
        projectType?.enable({ emitEvent: false });
        projectManager?.enable({ emitEvent: false });
        billingCompany?.enable({ emitEvent: false });
        paymentDays?.enable({ emitEvent: false });
        internal?.enable({ emitEvent: false });
        oldCode?.enable({ emitEvent: false });

        // Contact fields
        contactMode?.enable({ emitEvent: false });

        this.onContactModeChange(
          contactMode?.value || 'SEL-CONT'
        );
      }
    }

    else if (mode === 'add') {
      this.projectForm.get('project')?.clearValidators();

      if (clientSelected) {
        client?.enable({ emitEvent: false });
        projectCode?.enable({ emitEvent: false });
        projectDescription?.enable({ emitEvent: false });
        projectType?.enable({ emitEvent: false });
        projectManager?.enable({ emitEvent: false });
        billingCompany?.enable({ emitEvent: false });
        paymentDays?.enable({ emitEvent: false });
        internal?.enable({ emitEvent: false });
        oldCode?.enable({ emitEvent: false });

        contactMode?.enable({ emitEvent: false });
        this.onContactModeChange(contactMode?.value || 'SEL-CONT');
      }

      else {
        client?.enable({ emitEvent: false });

        projectCode?.disable({ emitEvent: false });
        projectDescription?.disable({ emitEvent: false });
        projectType?.disable({ emitEvent: false });
        projectManager?.disable({ emitEvent: false });
        billingCompany?.disable({ emitEvent: false });

        paymentDays?.setValue(30, { emitEvent: false });
        paymentDays?.disable({ emitEvent: false });

        internal?.disable({ emitEvent: false });
        oldCode?.disable({ emitEvent: false });

        contactMode?.disable({ emitEvent: false });

        availableContacts?.disable({ emitEvent: false });
        contact?.disable({ emitEvent: false });
        address?.disable({ emitEvent: false });
        telephone?.disable({ emitEvent: false });
        country?.disable({ emitEvent: false });
        state?.disable({ emitEvent: false });
      }
    }

    else {
      this.projectForm.get('project')?.setValidators([Validators.required]);
      this.projectForm.disable({ emitEvent: false });
    }

    this.projectForm.get('project')?.updateValueAndValidity({ emitEvent: false });
    this.projectForm.updateValueAndValidity({ emitEvent: false });
  }

  private enableEditFields(): void {
    // Keep project code disabled
    this.projectForm.get('projectCode')?.disable({ emitEvent: false });

    // Enable project details
    this.projectForm.get('projectDescription')?.enable({ emitEvent: false });
    this.projectForm.get('projectType')?.enable({ emitEvent: false });
    this.projectForm.get('projectManager')?.enable({ emitEvent: false });
    this.projectForm.get('billingCompany')?.enable({ emitEvent: false });
    this.projectForm.get('paymentDays')?.enable({ emitEvent: false });
    this.projectForm.get('internal')?.enable({ emitEvent: false });
    this.projectForm.get('oldCode')?.enable({ emitEvent: false });

    // Enable contact section
    this.projectForm.get('contactMode')?.enable({ emitEvent: false });

    this.onContactModeChange(
      this.projectForm.get('contactMode')?.value || 'SEL-CONT'
    );
  }

  onModeChange(newMode: ProjectDetailsMode): void {
    this.stateService.setMode(newMode);
    this.stateService.setSelectedIndex(0);

    if (newMode === 'add') {
      this.router.navigate([URLPaths.ROUTE_PROJECT_DETAILS + URLPaths.ROUTE_PROJECT_DETAILS_ADD]);
    } else if (newMode === 'edit') {
      this.router.navigate([URLPaths.ROUTE_PROJECT_DETAILS + URLPaths.ROUTE_PROJECT_DETAILS_EDIT]);
    }
  }

  async onClientChange(client: string): Promise<void> {
    if (!client) {
      this.projectsList = [];
      this.projectCodeOptions = [];
      this.availableContactsList = [];
      this.rawClientData = {};

      this.stateService.setSelectedClient('');
      this.stateService.setAddOtherDetailsDisabled(true);

      this.projectForm.patchValue({
        project: null,
        projectCode: '',
        projectDescription: '',
        projectManager: null,
        billingCompany: null,
        projectType: null,
        internal: false,
        oldCode: false,
        availableContacts: null,
        contact: '',
        address: '',
        telephone: '',
        country: null,
        state: null
      }, { emitEvent: false });

      this.statesList = [];
      this.configureFormForMode(this.mode);
      this.cdr.detectChanges();
      return;
    }

    this.stateService.setSelectedClient(client);

    // Reset previously selected project and all project-related fields
    this.projectForm.patchValue({
      project: null,
      projectCode: '',
      shortCode: '',
      projectDescription: '',
      projectManager: null,
      billingCompany: null,
      projectType: null,
      paymentDays: 30,
      internal: false,
      oldCode: false,
      oldProject: '',
      availableContacts: null,
      contact: '',
      address: '',
      telephone: '',
      country: null,
      state: null
    }, {
      emitEvent: false
    });

    // Clear dependent lists/state
    this.projectCodeOptions = [];
    this.availableContactsList = [];
    this.statesList = [];


    try {
      const rawData = await this.apiService.getProjectsAndContacts(client);
      this.rawClientData = rawData?.[client] || rawData || {};

      const projects = Array.isArray(this.rawClientData.project_details)
        ? this.rawClientData.project_details
        : [];

      this.projectsList = projects
        .map((p: any) => typeof p === 'string' ? p : p.project ?? p.Project ?? p.projectName ?? p.projectCode ?? '')
        .filter(Boolean);

      this.projectCodeOptions = projects
        .map((p: any) => {
          const display = typeof p === 'string' ? p : p.project ?? p.Project ?? p.projectName ?? p.projectCode ?? '';
          const code = typeof p === 'string'
            ? this.extractProjectCode(display)
            : (p.projectCode ?? p.code ?? this.extractProjectCode(display));
          return code ? { label: code, value: code, disabled: true } : null;
        })
        .filter((p: any) => !!p);

      const contacts = Array.isArray(this.rawClientData.contact_details)
        ? this.rawClientData.contact_details
        : [];

      this.availableContactsList = contacts
        .map((c: any) => typeof c === 'string' ? c : c.contact ?? c.Contact ?? '')
        .filter(Boolean);

      const internalClient =
        (this.clientsList.find((c: string) => c === client) || '').toUpperCase().includes('INTERNAL')
        || !!this.rawClientData?.internal;

      this.projectForm.patchValue({ internal: internalClient }, { emitEvent: false });
      this.stateService.setIsInternal(internalClient);

      if (this.mode === 'edit' && this.availableContactsList.length === 1) {
        const contactName = this.availableContactsList[0];
        this.projectForm.patchValue({ availableContacts: contactName }, { emitEvent: false });
        this.onAvailableContactChange(contactName);
      }

      this.configureFormForMode(this.mode);
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error fetching client projects/contacts:', e);
    }
  }

  private extractProjectCode(project: string): string {
    if (!project) return '';
    const parts = project.split(' - ');
    return parts.length > 1 ? parts[parts.length - 1].trim() : project.trim();
  }

  async onProjectChange(project: string): Promise<void> {
    if (!project) {
      this.stateService.setSelectedProject('');
      this.stateService.setViewOtherDetailsDisabled(true);
      return;
    }

    this.stateService.setSelectedProject(project);

    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();

    try {
      const projects = this.rawClientData.project_details || [];

      const matched = projects.find((p: any) => {
        const display = typeof p === 'string' ? p : p.project ?? p.Project ?? p.projectName ?? p.projectCode ?? '';
        return display === project;
      });

      let projectData: any = {};
      if (matched) {
        projectData = typeof matched === 'string' ? {} : matched;

        this.projectForm.patchValue({
          projectCode: projectData.projectCode || projectData.code || this.extractProjectCode(project),
          shortCode: projectData.shortCode || projectData.short_code || '',
          projectDescription: projectData.description || projectData.projectDescription || '',
          projectManager: projectData.manager || projectData.projectManager || null,
          billingCompany: projectData.company || projectData.billingCompany || null,
          projectType: projectData.projectType || projectData.projectTypeName || null,
          paymentDays: projectData.paymentDays ?? 30,
          internal: typeof projectData.internal === 'boolean'
            ? projectData.internal
            : (projectData.internal === 'YES' || projectData.internal === 1),
          oldCode: typeof projectData.oldCode === 'boolean'
            ? projectData.oldCode
            : (projectData.oldCode === 'YES' || projectData.oldCode === 1),
          oldProject: projectData.oldProject || ''
        }, { emitEvent: false });

        if (projectData.contact) {
          this.projectForm.patchValue({ availableContacts: projectData.contact }, { emitEvent: false });
          this.onAvailableContactChange(projectData.contact);
        }
      }

      // Enable all fields after project is selected
      if (this.mode === 'edit') {
        this.enableEditFields();
      }

      const tableData = await this.apiService.getProjectDetailsTables(clientCode, projectCode);

      // Lookup contact data from client contacts
      const contactObj = this.rawClientData?.contact_details?.find(
        (c: any) => (c.contact ?? c.Contact ?? c) === (projectData.contact || this.projectForm.get('contact')?.value)
      );

      const combinedTableData: ProjectDetailsTableData = {
        ...(tableData || {}),
        project_details: {
          ...projectData,
          client: this.projectForm.get('client')?.value,
          contactData: contactObj
        },
        invoiceDetails: projectData.invoiceDetails || projectData.invoice_details || {
          invoiceDescription: projectData.description || projectData.projectDescription || '',
          invoiceFormat: projectData.invoiceFormat || 'Free Format',
          freeFormatDescription: projectData.freeFormatInvoiceDescription || projectData.freeFormatDescription || projectData.description || '',
          projectHourlyRate: projectData.rate || projectData.projectHourlyRate || 0,
          showHourlyBreakdown: projectData.showHourBreakdown || projectData.showHourlyBreakdown || false,
          projectDiscount: projectData.discount || projectData.projectDiscount || 0,
          salesTax: projectData.vatRate || projectData.salesTax,
          purchaseOrderNo: projectData.purchaseOrderNumber || projectData.purchaseOrderNo,
          invoiceContact: projectData.invoiceContact || projectData.contact,
          invoiceContactAddress: projectData.invoiceAddress || projectData.invoiceContactAddress || contactObj?.address || '',
          charge: projectData.charge !== undefined ? projectData.charge : true,
          noVat: projectData.noVat || false,
          clientVatNo: projectData.clientVatNumber || projectData.clientVatNo,
          cnpj: projectData.cnpj,
          typeOfService: projectData.typeOfService
        }
      };

      this.stateService.setTableData(combinedTableData);
      this.stateService.setViewOtherDetailsDisabled(false);
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error fetching project details:', e);
    }
  }

  onAvailableContactChange(contactName: string): void {
    if (!contactName) return;

    const contacts = this.rawClientData.contact_details || [];

    const matched = contacts.find((c: any) => {
      const name = typeof c === 'string' ? c : c.contact ?? c.Contact ?? '';
      return name === contactName;
    });

    if (!matched) return;

    const contactData = typeof matched === 'string' ? {} : matched;
    const selectedCountry = contactData.country ?? contactData.Country ?? null;
    const selectedState = contactData.state ?? contactData.State ?? null;

    this.projectForm.patchValue({
      contact: contactData.contact ?? contactData.Contact ?? contactName,
      address: contactData.address ?? contactData.Address ?? '',
      telephone: contactData.telephone ?? contactData.Telephone ?? '',
      country: selectedCountry,
      state: null
    }, { emitEvent: false });

    if (selectedCountry) {
      this.onCountryChange(selectedCountry);

      if (selectedState && this.statesList.includes(selectedState)) {
        this.projectForm.patchValue({ state: selectedState }, { emitEvent: false });
      }
    }

    this.cdr.detectChanges();
  }

  onContactModeChange(mode: 'ADD-NEW' | 'SEL-CONT'): void {
    this.projectForm.patchValue({ contactMode: mode }, { emitEvent: false });

    const availableContacts = this.projectForm.get('availableContacts');
    const contact = this.projectForm.get('contact');
    const address = this.projectForm.get('address');
    const telephone = this.projectForm.get('telephone');
    const country = this.projectForm.get('country');

    if (mode === 'ADD-NEW') {
      availableContacts?.disable({ emitEvent: false });

      contact?.enable({ emitEvent: false });
      address?.enable({ emitEvent: false });
      telephone?.enable({ emitEvent: false });
      country?.enable({ emitEvent: false });

      this.projectForm.patchValue({
        availableContacts: null,
        contact: '',
        address: '',
        telephone: '',
        country: null,
        state: null
      }, { emitEvent: false });

      this.statesList = [];

      this.projectForm.get('state')?.disable({ emitEvent: false });
      this.projectForm.get('state')?.clearValidators();
      this.projectForm.get('state')?.updateValueAndValidity({ emitEvent: false });
    }

    else {
      availableContacts?.enable({ emitEvent: false });

      /*
        IMPORTANT FIX:
        Old UI keeps these fields enabled even when
        "Select from existing contacts" is selected.
        Therefore we DO NOT disable them.

        NOTE: the component alone can't make these
        editable - if project-info-tab.component.html
        still has [readonly]="projectForm.get('contactMode')
        ?.value === 'SEL-CONT'" on the contact/address/
        telephone inputs, that readonly binding will still
        block typing even though these controls are enabled
        here. That binding needs to be removed too.
      */
      contact?.enable({ emitEvent: false });
      address?.enable({ emitEvent: false });
      telephone?.enable({ emitEvent: false });
      country?.enable({ emitEvent: false });

      const selectedContact = this.projectForm.get('availableContacts')?.value;

      if (selectedContact) {

        this.onAvailableContactChange(selectedContact);

      } else if (
        this.mode === 'edit' &&
        this.availableContactsList.length > 0
      ) {

        const firstContact = this.availableContactsList[0];

        this.projectForm.patchValue(
          { availableContacts: firstContact },
          { emitEvent: false }
        );

        this.onAvailableContactChange(firstContact);

      }

      const selectedCountry = this.projectForm.get('country')?.value;

      if (selectedCountry) {
        this.onCountryChange(selectedCountry);
      } else {
        this.statesList = [];
        this.projectForm.patchValue({ state: null }, { emitEvent: false });
        this.projectForm.get('state')?.disable({ emitEvent: false });
      }
    }

    this.cdr.detectChanges();
  }

  onCountryChange(country: string): void {
    const stateControl = this.projectForm.get('state');

    if (!country) {
      this.statesList = [];
      stateControl?.setValue(null, { emitEvent: false });
      stateControl?.disable({ emitEvent: false });
      stateControl?.clearValidators();
      stateControl?.updateValueAndValidity({ emitEvent: false });
      return;
    }

    this.stateService.setSelectedCountry(country);

    const currency = this.cascadeService.getCurrencyForCountry(country);
    const taxType = this.cascadeService.getTaxTypeForCountry(country);

    this.stateService.setCurrencyLabel(currency || '');
    this.stateService.setTaxTypeLabel(
      taxType
        ? (taxType.includes('(%') ? taxType : `${taxType}: (%)`)
        : 'VAT Rate: (%)'
    );
    const selectedCountryObject = this.allStatesList.find((item: any) =>
      this.normalizeValue(item.countryDescription) === this.normalizeValue(country)
      || this.normalizeValue(item.countryName) === this.normalizeValue(country)
    );

    const availableStates = selectedCountryObject?.states || [];

    if (availableStates.length > 0) {
      this.statesList = [...availableStates];
      this.statesList.sort((a: any, b: any) => String(a.stateName).localeCompare(String(b.stateName)));

      stateControl?.enable({ emitEvent: false });
      stateControl?.setValidators([Validators.required]);

      const currentState = stateControl?.value;

      if (currentState) {
        const stateExists = this.statesList.some((state: any) =>
          state.stateId === currentState || state.stateName === currentState
        );

        if (!stateExists) {
          stateControl?.setValue(null, { emitEvent: false });
        }
      }
    }

    else {
      this.statesList = [];
      stateControl?.setValue(null, { emitEvent: false });
      stateControl?.disable({ emitEvent: false });
      stateControl?.clearValidators();
    }

    stateControl?.updateValueAndValidity({ emitEvent: false });
  }

 onStateChange(): void {
  const country = this.projectForm.get('country')?.value;
  const state = this.projectForm.get('state')?.value;

  if (country) {
    const taxPercentage = this.cascadeService.getTaxPercentage(
      country,
      state || ''
    );

    this.stateService.setDefaultTaxRate(taxPercentage);
  }
}

  restrictPRCodeLength(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    if (
      input &&
      input.value.length >= 4 &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.key)
    ) {
      event.preventDefault();
    }
  }

  // ==========================================
  // BUILD CONTACT PAYLOAD
  // Shared by onSubmitProjectDetails() and
  // onSaveProjectDetails() - matches the shape the
  // original addContact()/updateContact() sent:
  // (contact, client, address, telephone, mileage,
  // stateId, countryId), resolved via the same
  // getStateId/getCountryId lookups.
  // ==========================================

  private async buildContactPayload(formVal: any, clientCode: string): Promise<any> {
    return {
      contact: formVal.contact,
      client: clientCode,
      address: formVal.address || '',
      telephone: formVal.telephone || '',
      // No "mileageRate" field exists on this form (it's owned
      // by the Invoice tab). Original code read formData.mileageRate
      // here too, which was always undefined from this form
      // (parseInt(undefined) -> NaN), so we pass null instead.
      mileage: null,
      stateId: await this.apiService.getStateId(formVal.country, formVal.state || ''),
      countryId: await this.apiService.getCountryId(formVal.country)
    };
  }

  // ==========================================
  // SYNC CONTACT
  // Creates the contact when adding a brand new one, or
  // pushes edits to the backend when an existing contact
  // was selected and its fields were changed.
  //
  // This MUST run before saveProject/updateProject - the
  // project record references this contact by name, so if
  // the contact doesn't exist yet the project save fails
  // with a foreign-key / integrity constraint error (this
  // was the "23000 ... Creation Failed" error - the earlier
  // version of this file never called saveContact/
  // updateContact at all).
  //
  // Mirrors the original addProject()/updateProject(),
  // which always called addContact()/updateContact() first.
  // ==========================================

  private async syncContact(formVal: any, clientCode: string): Promise<void> {
    const contactPayload = await this.buildContactPayload(formVal, clientCode);

    if (formVal.contactMode === 'ADD-NEW') {
      await firstValueFrom(this.apiService.saveContact(contactPayload));
    } else {
      await firstValueFrom(this.apiService.updateContact(contactPayload));
    }
  }

  async onSubmitProjectDetails(): Promise<void> {
    Object.keys(this.projectForm.controls).forEach(key => {
      const control = this.projectForm.get(key);
      if (control?.invalid) {
        console.log('INVALID FIELD:', key, control.errors);
      }
    });

    if (this.projectForm.invalid) {
      this.projectForm.markAllAsTouched();
      alert('Please fill all the required details.');
      return;
    }

    const formVal = this.projectForm.getRawValue();
    const clientCode = this.stateService.extractCode(formVal.client);
    const projectCode = formVal.projectCode?.toUpperCase()?.trim();

    const projectPayload = {
      client: clientCode,
      projectCode: projectCode,
      description: formVal.projectDescription,
      contact: formVal.contact,
      manager: formVal.projectManager,
      discount: null,
      discountType: null,
      invoiceDesc: null,
      mileage: null,
      purchaseOrderNumber: null,
      rate: null,
      showHourBreakdown: null,
      vatRate: null,
      charge: false,
      oldCode: commonFunctions.booleanToInteger(formVal.oldCode),
      promptPaymentPeriodDuration: null,
      promptPaymentPeriodDiscount: null,
      promptPaymentPeriodType: null,
      loginName: this.stateService.loginName,
      internal: commonFunctions.booleanToInteger(formVal.internal),
      noVat: null,
      clientVatNumber: null,
      companyId: await this.apiService.getCompanyId(formVal.billingCompany),
      invoiceFormat: null,
      invoiceContact: null,
      invoiceAddress: null,
      freeFormatInvoiceDescription: null,
      paymentDays: parseInt(formVal.paymentDays, 10),
      projectTypeId: await this.apiService.getProjectTypeId(formVal.projectType),
      sensitive: 1,
      cnpj: null,
      typeOfService: null,
      oldProject: projectCode
    };

    try {
      // Contact must exist before the project references it -
      // see syncContact() doc comment above.
      await this.syncContact(formVal, clientCode);

      console.log('Saving Project Payload:', projectPayload);

      const saveRes = await firstValueFrom(this.apiService.saveProject(projectPayload));

      console.log('Project Save Response:', saveRes);

      this.stateService.showNotification(saveRes);

      if (saveRes?.Type === 'success' || saveRes?.type === 'success') {
        this.stateService.setSelectedClient(formVal.client);
        this.stateService.setSelectedProject(projectCode);
        this.stateService.setAddOtherDetailsDisabled(false);
        this.stateService.setUnsavedChanges(false);

        await this.apiService.getProjectsAndContacts(formVal.client);

        const tableData = await this.apiService.getProjectDetailsTables(
          clientCode,
          projectCode
        );

        const combinedTableData: ProjectDetailsTableData = {
          ...(tableData || {}),
          project_details: {
            client: formVal.client,
            contact: formVal.contact,
            contactData: {
              contact: formVal.contact,
              address: formVal.address || '',
              telephone: formVal.telephone || '',
              country: formVal.country || '',
              state: formVal.state || ''
            }
          },
          invoiceDetails: {
            invoiceContact: formVal.contact,
            invoiceContactAddress: formVal.address || ''
          }
        };

        this.stateService.setTableData(combinedTableData);
      }
    } catch (err: any) {
      console.error('PROJECT SAVE ERROR:', err);

      this.stateService.showNotification(
        err?.error || { Type: 'failure', Message: 'Failed to create project' }
      );
    }
  }

  onProjectCodeChange(event: any): void {
    console.log('Project Code Event:', event);
    console.log('Project Code Form Value:', this.projectForm.get('projectCode')?.value);
  }

  addProjectCode = (term: string) => {
    const code = term.toUpperCase().trim().slice(0, 4);
    return { label: code, value: code };
  };

  async onSaveProjectDetails(): Promise<void> {
    if (this.projectForm.invalid) {
      this.projectForm.markAllAsTouched();
      alert('Please fill all the required details.');
      return;
    }

    const formVal = this.projectForm.getRawValue();
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();

    const projectPayload = {
      client: clientCode,
      projectCode: projectCode,
      description: formVal.projectDescription,
      contact: formVal.contact,
      manager: formVal.projectManager,
      discount: null,
      discountType: 'Percentage',
      invoiceDesc: null,
      mileage: null,
      purchaseOrderNumber: '',
      rate: null,
      showHourBreakdown: null,
      vatRate: 0,
      charge: null,
      oldCode: commonFunctions.booleanToInteger(formVal.oldCode),
      promptPaymentPeriodDuration: null,
      promptPaymentPeriodDiscount: null,
      promptPaymentPeriodType: null,
      loginName: this.stateService.loginName,
      internal: commonFunctions.booleanToInteger(formVal.internal),
      noVat: false,
      clientVatNumber: null,
      companyId: await this.apiService.getCompanyId(formVal.billingCompany),
      invoiceFormat: null,
      invoiceContact: null,
      invoiceAddress: null,
      freeFormatInvoiceDescription: null,
      paymentDays: parseInt(formVal.paymentDays, 10),
      projectTypeId: await this.apiService.getProjectTypeId(formVal.projectType),
      sensitive: 1,
      cnpj: null,
      typeOfService: null,
      oldProject: projectCode
    };

    try {
      // Contact must exist / be up to date before updateProject
      // runs - see syncContact() doc comment above.
      await this.syncContact(formVal, clientCode);

      const updateRes = await this.apiService.updateProject(projectPayload).toPromise();

      this.stateService.showNotification(updateRes);
      this.stateService.setUnsavedChanges(false);

      const tableData = await this.apiService.getProjectDetailsTables(clientCode, projectCode);
      this.stateService.setTableData(tableData);
    } catch (err: any) {
      this.stateService.showNotification(
        err.error || { Type: 'failure', Message: 'Failed to update project' }
      );
    }
  }

  onClearProjectDetails(): void {
    this.projectForm.reset({
      paymentDays: 30,
      internal: false,
      oldCode: false,
      contactMode: 'SEL-CONT'
    });

    this.statesList = [];
    this.stateService.resetAllState();
    this.configureFormForMode(this.mode);
  }

  onNextTab(): void {
    this.stateService.setSelectedIndex(1);
  }

  async onSubmitThemeAllocation(): Promise<void> {
    if (this.themeForm.invalid) {
      this.themeForm.markAllAsTouched();
      return;
    }

    const formVal = this.themeForm.value;
    const clientCode = this.stateService.getSelectedClientCode();
    const projectCode = this.stateService.getSelectedProjectCode();
    const themeCode = await this.apiService.getProjectThemeCode(formVal.theme);

    const payload = {
      client: clientCode,
      project: projectCode,
      allocationPercent: parseInt(formVal.allocation, 10),
      theme: themeCode
    };

    try {
      const res = await this.apiService.saveThemeAllocation(payload).toPromise();
      this.stateService.showNotification(res);
      this.themeForm.reset();

      const tableData = await this.apiService.getProjectDetailsTables(clientCode, projectCode);
      this.stateService.setTableData(tableData);
    } catch (err: any) {
      this.stateService.showNotification(
        err.error || { Type: 'failure', Message: 'Failed to add theme allocation' }
      );
    }
  }

  onOpenThemeAllocationDialog(): void {
    console.log('Themes before opening dialog:', this.themesList);

    this.dialog.open(ThemeAllocationDailogComponent, {
      width: '750px',
      height: '500px',
      data: {
        gridType: 'themeAllocation',
        mode: this.mode,
        themeList: this.themesList
      }
    });
  }
}